import { BANNED_URLS, PAGE_TYPES } from '../assistant-config';
import { getHostname, isLocalDevHost } from './window-utils';

export const getPortalRootNode = () => {
  return document?.querySelector('#root');
};

export const getStickyATCElement = () => {
  // Query elements matching the markup added by the Sticky component
  const buyboxMatch = document.querySelector(
    'div[data-component^=buybox\\:Buybox\\:v] [style^=min-height] > [class*=sticky]'
  );
  const applianceBuyboxMatch = document.querySelector(
    'div[data-component^=buybox\\:MajorApplianceBuyBox\\:v] [style^=min-height] > [class*=sticky]'
  );
  const buyboxActionsMatch = document.querySelector(
    'div[data-component^=buybox\\:BuyboxActions\\:v] [style^=min-height] > [class*=sticky]'
  );
  const buyboxSimpleMatch = document.querySelector(
    'div[data-component^=Buybox] [style^=min-height] > [class*=sticky]'
  );

  // Use the more specific section if available
  if (buyboxActionsMatch) return buyboxActionsMatch;

  if (applianceBuyboxMatch) return applianceBuyboxMatch;

  if (buyboxSimpleMatch) return buyboxSimpleMatch;

  // Use the generic (older) version as fallback
  if (buyboxMatch) return buyboxMatch;

  return null;
};

export const getPath = () => {
  return typeof window !== 'undefined' ? new URL(window.document.documentURI).pathname : '';
};

// ⚠ Two page-type vocabularies exist, do not merge them — see
// .claude/context/page-classification.md.
const legacyCompatibilityPageType = () => {
  const pathArray = getPath()?.split('/') || [];

  if (pathArray[1] === 'c') return PAGE_TYPES.CONTENT;
  if (pathArray[1] === 'p') {
    if (pathArray[2] === 'sets') return PAGE_TYPES.BIP;
    return PAGE_TYPES.PIP;
  }
  if (pathArray[1] === 's') return PAGE_TYPES.SEARCH;
  if (pathArray[1] === 'b') return PAGE_TYPES.BROWSE;
  if (pathArray[1] === '') return PAGE_TYPES.HOME;

  return PAGE_TYPES.UNKNOWN;
};

// ⚠ Session helpers do not live here — see .claude/context/page-classification.md.

export const getIsLegacyCompatiblePage = () => {
  if (BANNED_URLS.includes(getPath())) {
    return false;
  }

  return [
    PAGE_TYPES.HOME,
    PAGE_TYPES.PIP,
    PAGE_TYPES.BROWSE,
    PAGE_TYPES.SEARCH,
    PAGE_TYPES.CONTENT,
  ].includes(legacyCompatibilityPageType());
};

// Exact paths or /*-prefixes, matched at path end — see
// .claude/context/page-classification.md.
const parseExcludedEntries = (configValue) => {
  if (!configValue) {
    return [];
  }

  return configValue
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
};

const matchesExcludedEntry = (path, entry) => {
  if (!entry.endsWith('/*')) {
    return path.endsWith(entry);
  }

  return path.includes(entry.slice(0, -1));
};

// A second instance inside a frame is never wanted — see
// .claude/context/page-classification.md.
export const getIsInIframe = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.self !== window.top;
  } catch {
    // Reading window.top can throw behind a cross-origin parent, which only ever
    // happens when we are framed.
    return true;
  }
};

export const getIsNewCompatiblePage = (excludedPagesConfig) => {
  const path = getPath();
  const excludedEntries = parseExcludedEntries(excludedPagesConfig);
  const isExcluded = excludedEntries.some((entry) => matchesExcludedEntry(path, entry));

  if (isExcluded) {
    return false;
  }

  return true;
};

export const waitForIdle = (callback, timeout = 3000) => {
  const run = () => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout });
    } else {
      // fallback for Safari/older browsers
      setTimeout(callback, 0);
    }
  };

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
};

export const HOST_CONSTANTS = {
  // eslint-disable-next-line
  ENV_HOST: typeof import.meta.env.MAA__DEFAULT_HOST__ !== 'undefined' ?  import.meta.env.MAA__DEFAULT_HOST__ : '',
  GRAPHQL_HOST: {
    DEV: 'https://apionline-dev.homedepotdev.com',
    STAGE: 'https://apionline-stage.homedepotdev.com',
    PROD: 'https://apionline.homedepot.com'
  },
  ENV: {
    DEV: 'hd-qa71',
    STAGE: 'hd-qa74',
    NONPROD: 'homedepotdev',
    PROD: 'homedepot',
  },
};

export const getGraphqlHost = () => {
  const currentHost = typeof window !== 'undefined' ? (window.location?.host ?? '').split('.') : [];
  const { ENV, GRAPHQL_HOST, ENV_HOST } = HOST_CONSTANTS;

  // The build's API override (MAA__DEFAULT_HOST__; .env.staging sets it for `pnpm stage`) applies
  // only on the local dev server, so a page on any other host never reads it.
  if (ENV_HOST.length > 0 && isLocalDevHost(getHostname())) return ENV_HOST;

  switch (true) {
  case currentHost.includes(ENV.DEV): {
    return GRAPHQL_HOST.DEV;
  }
  case currentHost.includes(ENV.STAGE): {
    return GRAPHQL_HOST.STAGE;
  }
  case currentHost.includes(ENV.NONPROD): {
    return GRAPHQL_HOST.STAGE;
  }
  case currentHost.includes(ENV.PROD): {
    return GRAPHQL_HOST.PROD;
  }
  default: {
    return GRAPHQL_HOST.PROD;
  }
  }
};